import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, MouseSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  StickyNote, Plus, ArrowLeft, MoreVertical, Pencil, Trash2, Copy, Share2, Lock,
  Users, X, Check, Loader2, GripVertical, ChevronUp, ChevronDown,
} from 'lucide-react';

// One draggable note card on the overview. Drag is bound ONLY to the ☰ handle so
// tapping the card still opens it and the three-dot menu keeps working normally.
const SortableNoteCard = ({ note, idx, count, writable, t, onOpen, onShare, onManageShare, onDelete, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: note.id, disabled: !writable });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : 'auto' };
  return (
    <div ref={setNodeRef} style={style}
      className="bg-white rounded-xl border overflow-hidden flex cursor-pointer hover:border-purple-300 transition-colors"
      onClick={() => onOpen(note.id)} data-testid={`notes-card-${note.id}`}>
      <span className="w-1.5 shrink-0" style={{ backgroundColor: note.color }} />
      <div className="flex-1 min-w-0 p-3">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-gray-900 truncate">{note.title}</p>
          {note.visibility === 'private'
            ? <Lock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            : <Users className="h-3.5 w-3.5 text-gray-400 shrink-0" />}
          {!note.isOwner && <Badge variant="secondary" className="text-[9px]">{t('notes.sharedTag', 'shared with you')}</Badge>}
        </div>
        {note.preview ? <p className="text-[12px] text-gray-500 truncate mt-0.5">{note.preview}</p> : null}
      </div>
      <div className="p-2 flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
        {writable && (
          <button type="button"
            className="shrink-0 flex items-center justify-center h-8 w-7 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none"
            aria-label={t('notes.dragHandle', 'Drag to reorder')} title={t('notes.dragHandle', 'Drag to reorder')}
            data-testid={`notes-card-drag-${note.id}`} {...attributes} {...listeners}>
            <GripVertical className="h-5 w-5" />
          </button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`notes-card-menu-${note.id}`}><MoreVertical className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onOpen(note.id)} data-testid={`notes-card-open-${note.id}`}><StickyNote className="h-4 w-4 mr-2" />{t('notes.open', 'Open')}</DropdownMenuItem>
            {writable && count > 1 && <>
              <DropdownMenuItem disabled={idx === 0} onClick={() => onMove(idx, -1)} data-testid={`notes-card-up-${note.id}`}><ChevronUp className="h-4 w-4 mr-2" />{t('notes.moveUp', 'Move up')}</DropdownMenuItem>
              <DropdownMenuItem disabled={idx === count - 1} onClick={() => onMove(idx, 1)} data-testid={`notes-card-down-${note.id}`}><ChevronDown className="h-4 w-4 mr-2" />{t('notes.moveDown', 'Move down')}</DropdownMenuItem>
            </>}
            <DropdownMenuItem onClick={() => onShare(note)} data-testid={`notes-card-share-${note.id}`}><Share2 className="h-4 w-4 mr-2" />{t('notes.share', 'Share')}</DropdownMenuItem>
            {note.canManage && <>
              <DropdownMenuItem onClick={() => onManageShare(note)} data-testid={`notes-card-manage-share-${note.id}`}><Users className="h-4 w-4 mr-2" />{t('notes.manageSharing', 'Manage sharing')}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={() => onDelete(note)} data-testid={`notes-card-delete-${note.id}`}><Trash2 className="h-4 w-4 mr-2" />{t('notes.delete', 'Delete')}</DropdownMenuItem>
            </>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const NOTE_PALETTE = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#d946ef', '#ec4899', '#f43f5e', '#64748b',
];

const NotesPanel = ({ calendarId, role }) => {
  const { t } = useTranslation();
  const writable = ['owner', 'admin', 'member'].includes(role);

  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [openNoteId, setOpenNoteId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // create/edit dialog
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({ title: '', body: '', color: NOTE_PALETTE[2], visibility: 'private' });
  const [savingNote, setSavingNote] = useState(false);

  // share dialog
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmails, setShareEmails] = useState([]);
  const [shareInput, setShareInput] = useState('');
  const [savingShare, setSavingShare] = useState(false);

  const [confirmDeleteNote, setConfirmDeleteNote] = useState(null);

  const loadNotes = useCallback(async () => {
    if (!calendarId) return;
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/calendar/calendars/${calendarId}/notes`);
      setNotes(r.data.notes || []);
    } catch (e) {
      toast.error(t('notes.loadError', 'Could not load notes'));
    } finally {
      setLoading(false);
    }
  }, [calendarId, t]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const openNote = async (id) => {
    setOpenNoteId(id);
    setDetailLoading(true);
    try {
      const r = await axiosInstance.get(`/calendar/notes/${id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(t('notes.openError', 'This note is not available'));
      setOpenNoteId(null);
      loadNotes();
    } finally {
      setDetailLoading(false);
    }
  };

  const closeNote = () => { setOpenNoteId(null); setDetail(null); loadNotes(); };

  // ---- manual reordering (persists server-side) ----
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const persistNoteOrder = async (newNotes) => {
    setNotes(newNotes);
    try {
      await axiosInstance.patch(`/calendar/calendars/${calendarId}/notes/reorder`, { noteIds: newNotes.map(n => n.id) });
    } catch (e) { toast.error(t('notes.reorderError', 'Could not reorder')); await loadNotes(); }
  };
  const handleNoteDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = notes.findIndex(n => n.id === active.id);
    const to = notes.findIndex(n => n.id === over.id);
    if (from === -1 || to === -1) return;
    await persistNoteOrder(arrayMove(notes, from, to));
  };
  const moveNote = async (index, dir) => {
    const arr = [...notes];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    await persistNoteOrder(arr);
  };

  // ---- CRUD ----
  const openCreate = () => {
    setEditingNote(null);
    setNoteForm({ title: '', body: '', color: NOTE_PALETTE[2], visibility: 'private' });
    setNoteDialogOpen(true);
  };
  const openEdit = (note) => {
    setEditingNote(note);
    setNoteForm({ title: note.title, body: note.body || '', color: note.color, visibility: note.visibility });
    setNoteDialogOpen(true);
  };
  const saveNote = async () => {
    if (!noteForm.title.trim()) return;
    setSavingNote(true);
    try {
      if (editingNote) {
        const payload = { title: noteForm.title.trim(), body: noteForm.body };
        if (editingNote.canManage) { payload.color = noteForm.color; payload.visibility = noteForm.visibility; }
        const r = await axiosInstance.patch(`/calendar/notes/${editingNote.id}`, payload);
        if (openNoteId === editingNote.id) setDetail(r.data);
        toast.success(t('notes.updated', 'Note updated'));
      } else {
        await axiosInstance.post(`/calendar/calendars/${calendarId}/notes`, {
          title: noteForm.title.trim(), body: noteForm.body, color: noteForm.color, visibility: noteForm.visibility,
        });
        toast.success(t('notes.created', 'Note created'));
      }
      setNoteDialogOpen(false);
      await loadNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notes.saveError', 'Could not save note'));
    } finally {
      setSavingNote(false);
    }
  };
  const deleteNote = async () => {
    const note = confirmDeleteNote;
    try {
      await axiosInstance.delete(`/calendar/notes/${note.id}`);
      toast.success(t('notes.deleted', 'Note deleted'));
      if (openNoteId === note.id) closeNote(); else loadNotes();
    } catch (e) {
      toast.error(t('notes.deleteError', 'Could not delete note'));
    } finally {
      setConfirmDeleteNote(null);
    }
  };

  // ---- copy / device share ----
  const noteText = (note) => `${note.title}${note.body ? `\n\n${note.body}` : ''}`;
  const copyNote = async (note) => {
    try {
      await navigator.clipboard.writeText(noteText(note));
      toast.success(t('notes.copied', 'Note copied to clipboard'));
    } catch (e) {
      toast.error(t('notes.copyError', 'Could not copy note'));
    }
  };
  const shareNote = async (note) => {
    const text = noteText(note);
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ title: note.title, text }); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('notes.shareCopied', 'Sharing not supported here — note copied instead'));
    } catch (e) {
      toast.error(t('notes.shareError', 'Could not share note'));
    }
  };

  // ---- in-app sharing ----
  const openShare = (note) => {
    setEditingNote(note);
    setShareEmails((note.sharedWithDetails || []).map(d => d.email).filter(Boolean));
    setShareInput('');
    setShareOpen(true);
  };
  const addShareEmail = () => {
    const e = shareInput.trim();
    if (e && !shareEmails.some(x => x.toLowerCase() === e.toLowerCase())) {
      setShareEmails([...shareEmails, e]);
    }
    setShareInput('');
  };
  const saveShare = async () => {
    setSavingShare(true);
    try {
      const r = await axiosInstance.patch(`/calendar/notes/${editingNote.id}/share`, { emails: shareEmails });
      toast.success(t('notes.shareSaved', 'Sharing updated'));
      setShareOpen(false);
      if (openNoteId === editingNote.id) setDetail(r.data);
      await loadNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notes.shareUpdateError', 'Could not update sharing'));
    } finally {
      setSavingShare(false);
    }
  };

  // ================= RENDER: NOTE DETAIL =================
  if (openNoteId) {
    return (
      <div className="space-y-3" data-testid="notes-detail">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={closeNote} data-testid="notes-back">
            <ArrowLeft className="h-4 w-4 mr-1" />{t('notes.back', 'Notes')}
          </Button>
        </div>
        {detailLoading || !detail ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="h-1.5 w-full" style={{ backgroundColor: detail.color }} />
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h2 className="text-base font-bold text-gray-900 break-words" data-testid="notes-detail-title">{detail.title}</h2>
                  {detail.visibility === 'private'
                    ? <Badge variant="secondary" className="text-[10px] gap-1 shrink-0"><Lock className="h-3 w-3" />{t('notes.private', 'Private')}</Badge>
                    : <Badge variant="secondary" className="text-[10px] gap-1 shrink-0"><Users className="h-3 w-3" />{t('notes.shared', 'Shared')}</Badge>}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" data-testid="notes-detail-menu"><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => copyNote(detail)} data-testid="notes-detail-copy"><Copy className="h-4 w-4 mr-2" />{t('notes.copy', 'Copy text')}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => shareNote(detail)} data-testid="notes-detail-share"><Share2 className="h-4 w-4 mr-2" />{t('notes.share', 'Share')}</DropdownMenuItem>
                    {detail.canEdit && <DropdownMenuItem onClick={() => openEdit(detail)} data-testid="notes-detail-edit"><Pencil className="h-4 w-4 mr-2" />{t('notes.edit', 'Edit')}</DropdownMenuItem>}
                    {detail.canManage && <DropdownMenuItem onClick={() => openShare(detail)} data-testid="notes-detail-manage-share"><Users className="h-4 w-4 mr-2" />{t('notes.manageSharing', 'Manage sharing')}</DropdownMenuItem>}
                    {detail.canManage && <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600" onClick={() => setConfirmDeleteNote(detail)} data-testid="notes-detail-delete"><Trash2 className="h-4 w-4 mr-2" />{t('notes.delete', 'Delete')}</DropdownMenuItem>
                    </>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {detail.visibility === 'shared' && (detail.sharedWithDetails || []).length > 0 && (
                <p className="text-[11px] text-gray-500 mt-2">{t('notes.sharedWith', 'Shared with')}: {(detail.sharedWithDetails || []).map(d => d.name || d.email).join(', ')}</p>
              )}
              {!detail.canEdit && (
                <p className="text-[11px] text-amber-600 mt-2" data-testid="notes-readonly-note">{t('notes.readOnly', 'You have read-only access to this note.')}</p>
              )}
              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words mt-3 min-h-[80px]" data-testid="notes-detail-body">
                {detail.body || <span className="text-gray-400">{t('notes.empty', 'This note has no text yet.')}</span>}
              </p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => copyNote(detail)} data-testid="notes-copy"><Copy className="h-4 w-4 mr-1" />{t('notes.copy', 'Copy text')}</Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => shareNote(detail)} data-testid="notes-share"><Share2 className="h-4 w-4 mr-1" />{t('notes.share', 'Share')}</Button>
                {detail.canEdit && <Button size="sm" className="flex-1" onClick={() => openEdit(detail)} data-testid="notes-edit"><Pencil className="h-4 w-4 mr-1" />{t('notes.edit', 'Edit')}</Button>}
              </div>
            </div>
          </div>
        )}
        {renderDialogs()}
      </div>
    );
  }

  // ================= DIALOGS =================
  function renderDialogs() {
    return (
      <>
        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="notes-form-dialog">
            <DialogHeader>
              <DialogTitle>{editingNote ? t('notes.editNote', 'Edit note') : t('notes.createNote', 'Create note')}</DialogTitle>
              <DialogDescription>{t('notes.formHint', 'Give your note a title and text. Notes are private unless you share them.')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px]">{t('notes.title', 'Title')}</Label>
                <Input value={noteForm.title} maxLength={120} autoFocus
                  onChange={e => setNoteForm(f => ({ ...f, title: e.target.value }))} data-testid="notes-form-title" />
              </div>
              <div>
                <Label className="text-[11px]">{t('notes.text', 'Text')}</Label>
                <Textarea value={noteForm.body} rows={7} maxLength={20000}
                  onChange={e => setNoteForm(f => ({ ...f, body: e.target.value }))}
                  placeholder={t('notes.textPlaceholder', 'Write your note here…')} data-testid="notes-form-body" />
              </div>
              {(!editingNote || editingNote.canManage) && (
                <>
                  <div>
                    <Label className="text-[11px]">{t('notes.colour', 'Colour')}</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {NOTE_PALETTE.map(c => (
                        <button key={c} type="button" onClick={() => setNoteForm(f => ({ ...f, color: c }))}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${noteForm.color === c ? 'border-gray-900' : 'border-transparent'}`}
                          style={{ backgroundColor: c }} data-testid={`notes-form-color-${c}`}>
                          {noteForm.color === c && <Check className="h-3 w-3 text-white" />}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px]">{t('notes.privacy', 'Privacy')}</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <button type="button" onClick={() => setNoteForm(f => ({ ...f, visibility: 'private' }))}
                        className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm ${noteForm.visibility === 'private' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'}`}
                        data-testid="notes-form-private"><Lock className="h-4 w-4" />{t('notes.private', 'Private')}</button>
                      <button type="button" onClick={() => setNoteForm(f => ({ ...f, visibility: 'shared' }))}
                        className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm ${noteForm.visibility === 'shared' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'}`}
                        data-testid="notes-form-shared"><Users className="h-4 w-4" />{t('notes.shared', 'Shared')}</button>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">
                      {noteForm.visibility === 'private'
                        ? t('notes.privateHint', 'Only you can see this note.')
                        : t('notes.sharedHint', 'Pick calendar members to share it with after saving.')}
                    </p>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNoteDialogOpen(false)} data-testid="notes-form-cancel">{t('notes.cancel', 'Cancel')}</Button>
              <Button onClick={saveNote} disabled={savingNote || !noteForm.title.trim()} data-testid="notes-form-save">
                {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : t('notes.save', 'Save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent className="max-w-sm" data-testid="notes-share-dialog">
            <DialogHeader>
              <DialogTitle>{t('notes.manageSharing', 'Manage sharing')}</DialogTitle>
              <DialogDescription>{t('notes.shareDialogHint', 'Add the exact email of a calendar member. They must already belong to this calendar.')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-[11px]">{t('notes.memberEmail', 'Member email')}</Label>
                  <Input value={shareInput} onChange={e => setShareInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addShareEmail(); } }}
                    placeholder="name@example.com" data-testid="notes-share-input" />
                </div>
                <Button variant="outline" onClick={addShareEmail} data-testid="notes-share-add"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {shareEmails.length === 0 && <p className="text-[11px] text-gray-400">{t('notes.noneShared', 'Not shared with anyone yet.')}</p>}
                {shareEmails.map(e => (
                  <Badge key={e} variant="secondary" className="gap-1" data-testid={`notes-share-chip-${e}`}>
                    {e}
                    <button onClick={() => setShareEmails(shareEmails.filter(x => x !== e))}><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShareOpen(false)} data-testid="notes-share-cancel">{t('notes.cancel', 'Cancel')}</Button>
              <Button onClick={saveShare} disabled={savingShare} data-testid="notes-share-save">
                {savingShare ? <Loader2 className="h-4 w-4 animate-spin" /> : t('notes.save', 'Save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!confirmDeleteNote} onOpenChange={o => !o && setConfirmDeleteNote(null)}>
          <AlertDialogContent data-testid="notes-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('notes.deleteNote', 'Delete note')}?</AlertDialogTitle>
              <AlertDialogDescription>{t('notes.deleteConfirm', 'This permanently deletes the note. This cannot be undone.')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="notes-delete-cancel">{t('notes.cancel', 'Cancel')}</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={deleteNote} data-testid="notes-delete-confirm-btn">{t('notes.delete', 'Delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  // ================= RENDER: NOTES OVERVIEW =================
  return (
    <div className="space-y-3" data-testid="notes-overview">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-gray-500">{t('notes.overviewHint', 'Private and shared notes for this calendar.')}</p>
        <Button size="sm" onClick={openCreate} data-testid="notes-create-open"><Plus className="h-4 w-4 mr-1" />{t('notes.createNote', 'Create note')}</Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>
      ) : notes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border" data-testid="notes-empty">
          <StickyNote className="h-10 w-10 mx-auto text-gray-300" />
          <p className="text-sm text-gray-500 mt-2">{t('notes.emptyTitle', 'No notes yet')}</p>
          <p className="text-[12px] text-gray-400">{t('notes.emptyHint', 'Create your first private or shared note.')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {writable && notes.length > 1 && (
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 px-1" data-testid="notes-reorder-hint">
              <GripVertical className="h-3.5 w-3.5 text-gray-400" />
              {t('notes.reorderHint', 'Drag a note by its handle to reorder, or use the ▲ ▼ arrows')}
            </div>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleNoteDragEnd}>
            <SortableContext items={notes.map(n => n.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {notes.map((note, idx) => (
                  <SortableNoteCard key={note.id} note={note} idx={idx} count={notes.length} writable={writable} t={t}
                    onOpen={openNote} onShare={shareNote} onManageShare={openShare} onDelete={setConfirmDeleteNote} onMove={moveNote} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
      {renderDialogs()}
    </div>
  );
};

export default NotesPanel;
